import { LoadChildren, Route, Routes } from '@angular/router';
import { inject, Type } from '@angular/core';

import { RoutesEnum } from './routes.enum';
import { LOCALE_ROUTES_CONFIG } from './locale-routes.config';
import { AllowedLangsEnum } from './locale.enum';
import { createUrlTree } from 'core/utils/router';
import { LocaleService } from './locale.service';
import { filter, map, take } from 'rxjs';

export function getLazyModuleRoutes(route: RoutesEnum, commonParams: Partial<Route>): Routes {
  const routes: Routes = [];

  // add default route. example: '/home'
  routes.push({
    path: route,
    ...commonParams
  });

  for (const localePrefix in LOCALE_ROUTES_CONFIG[route]) {
    if (LOCALE_ROUTES_CONFIG?.[route]?.hasOwnProperty(localePrefix)) {
      // add route by language
      if (localePrefix !== AllowedLangsEnum.EN) {
        routes.push({
          // @ts-ignore
          path: `${LOCALE_ROUTES_CONFIG[route][localePrefix]}`,
          ...commonParams
        });
      }
    }
  }

  return routes;
}

// HOME PAGE
export function getHomeRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    path: '',
    data: {
      metaTitle: 'META_TAGS.HOME_TITLE',
      metaDesc: 'META_TAGS.HOME_DESCR',
      isLendingPage: true,
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  const routes: Routes = [];

  // add default route. example: '/home'
  routes.push({
    path: '',
    ...commonParams
  });

  Object.values(AllowedLangsEnum).forEach((locale: AllowedLangsEnum) => {
    if (locale !== AllowedLangsEnum.EN) {
      routes.push({
        ...commonParams,
        path: locale
      });
    }
  });
  return routes;
}

// FEATURES PAGES
export function getAccountingRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ACCOUNTING_TITLE',
      metaDesc: 'META_TAGS.ACCOUNTING_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'accounting',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ACCOUNTING, commonParams);
}

export function getWarehouseRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.WAREHOUSE_TITLE',
      metaDesc: 'META_TAGS.WAREHOUSE_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'warehouse',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.WAREHOUSE, commonParams);
}

export function getRetailRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.RETAIL_TITLE',
      metaDesc: 'META_TAGS.RETAIL_DESCR',
      allowPromoLine: true,
      headerDark: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.RETAIL, commonParams);
}

export function getPartnersRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.PARTNERS_TITLE',
      metaDesc: 'META_TAGS.PARTNERS_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'partners',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.PARTNERS, commonParams);
}

export function getSalesRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SALES_TITLE',
      metaDesc: 'META_TAGS.SALES_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'sales',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SALES, commonParams);
}

export function getProductsRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.PRODUCTS_TITLE',
      metaDesc: 'META_TAGS.PRODUCTS_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'products',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.PRODUCTS, commonParams);
}

export function getTeamRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.TEAM_TITLE',
      metaDesc: 'META_TAGS.TITLE_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'team'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.TEAM, commonParams);
}

export function getTimeTrackingRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.TIME_TRACKING_TITLE',
      metaDesc: 'META_TAGS.TIME_TRACKING_DESCR',
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'time-tracking'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.TIME_TRACKING, commonParams);
}

export function getServicesRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SERVICES_TITLE',
      metaDesc: 'META_TAGS.SERVICES_DESCR',
      allowPromoLine: true,
      hasCta: true,
      headerDark: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SERVICES, commonParams);
}

export function getTradeRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SOLUTION_TRADE_TITLE',
      metaDesc: 'META_TAGS.SOLUTION_TRADE_DESCR',
      hasCta: true,
      noindex: true,
      faqPageView: 'inventory-management'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.TRADE, commonParams);
}

export function getEcommerceRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ECOMMERCE_TITLE',
      metaDesc: 'META_TAGS.ECOMMERCE_DESCR',
      hasCta: true,
      allowPromoLine: true,
      headerDark: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ECOMMERCE, commonParams);
}

export function getAboutRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ABOUT_TITLE',
      metaDesc: 'META_TAGS.ABOUT_DESCR',
      hasCta: true,
      allowPromoLine: true,
      appBackgroundLight: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ABOUT, commonParams);
}

export function getAnalyticsRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    redirectTo: '',
    data: {
      metaTitle: 'META_TAGS.ANALYTICS_TITLE',
      metaDesc: 'META_TAGS.ANALYTICS_DESCR'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ANALYTICS, commonParams);
}

export function get404Routes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.404_TITLE',
      metaDesc: 'META_TAGS.404_DESCR',
      headerDark: true,
      noindex: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.NOT_PAGE, commonParams);
}


// SOLUTIONS PAGES
export function getAccountingSoftwareRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ACCOUNTING_TITLE',
      metaDesc: 'META_TAGS.ACCOUNTING_DESCR',
      isLendingPage: true,
      hasCta: true,
      noindex: true,
      faqPageView: 'accounting',
      indexForAtLocale: true
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ACCOUNTING_SOFTWARE, commonParams);
}

export function getInventoryManagementRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SOLUTION_TRADE_TITLE',
      metaDesc: 'META_TAGS.SOLUTION_TRADE_DESCR',
      isLendingPage: true,
      hasCta: true,
      appBackgroundLight: true,
      allowPromoLine: true,
      faqPageView: 'inventory-management'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.INVENTORY_MANAGEMENT, commonParams);
}

export function getEcommerceSoftwareRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SOLUTION_ECOMMERCE_TITLE',
      metaDesc: 'META_TAGS.SOLUTION_ECOMMERCE_DESCR',
      isLendingPage: true,
      hasCta: true,
      noindex: true,
      headerDark: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ECOMMERCE_SOFTWARE, commonParams);
}

export function getPointOfSaleRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SOLUTION_POINT_OF_SALE_TITLE',
      metaDesc: 'META_TAGS.SOLUTION_POINT_OF_SALE_DESCR',
      noindex: true,
      headerDark: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.POINT_OF_SALE, commonParams);
}

// SEO PAGES
export function getTradeSeoRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SOLUTION_TRADE_TITLE',
      metaDesc: 'META_TAGS.SOLUTION_TRADE_DESCR',
      hasCta: true,
      noindex: true,
      faqPageView: 'inventory-management'
    }
  };
  return getLazyModuleRoutes(RoutesEnum.TRADE_SEO, commonParams);
}

export function getEcommerceSeoRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ECOMMERCE_SEO_PAGE_TITLE',
      metaDesc: 'META_TAGS.ECOMMERCE_SEO_PAGE_DESCR',
      headerDark: true,
      noindex: true,
      hasCta: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ECOMMERCE_SEO, commonParams);
}

export function getErpSoftwareRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    redirectTo: '',
    data: {
      metaTitle: 'META_TAGS.ERP_SOFTWARE_SEO_TITLE',
      metaDesc: 'META_TAGS.ERP_SOFTWARE_SEO_DESCR',
      hasCta: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ERP_SOFTWARE, commonParams);
}

export function getOnlineStoreSeoRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    redirectTo: '',
    data: {
      metaTitle: 'META_TAGS.ONLINE_STORE_SEO_PAGE_TITLE',
      metaDesc: 'META_TAGS.ONLINE_STORE_SEO_PAGE_DESCR',
      hasCta: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ONLINE_STORE, commonParams);
}

export function getAccountingSeoRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ACCOUNTING_TITLE',
      metaDesc: 'META_TAGS.ACCOUNTING_DESCR',
      hasCta: true,
      noindex: true,
      faqPageView: 'accounting',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.ACCOUNTING_SEO, commonParams);
}

export function getRunpleFreeSeoRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.ACCOUNTING_TITLE',
      metaDesc: 'META_TAGS.ACCOUNTING_DESCR',
      hasCta: true,
      faqPageView: 'accounting',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.RUNPLE_FREE, commonParams);
}

// BLOG LEXICON PAGES
export function getBlogRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.BLOG_TITLE',
      metaDesc: 'META_TAGS.BLOG_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.BLOG, commonParams);
}

export function getBlogPageRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.BLOG_TITLE',
      metaDesc: 'META_TAGS.BLOG_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.BLOG_PAGE, commonParams);
}

export function getLexiconRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.LEXICON_TITLE',
      metaDesc: 'META_TAGS.LEXICON_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.LEXICON, commonParams);
}

export function getLexiconPageRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.LEXICON_TITLE',
      metaDesc: 'META_TAGS.LEXICON_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.LEXICON_PAGE, commonParams);
}

export function getServicePageRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SERVICE_TITLE',
      metaDesc: 'META_TAGS.SERVICE_DESCR',
      hideFooter: true,
      isExternalPage: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SERVICE, commonParams);
}

export function getAuthorRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.AUTHOR_TITLE',
      metaDesc: 'META_TAGS.AUTHOR_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  const routes: Routes = [];

  for (const localePrefix in LOCALE_ROUTES_CONFIG[RoutesEnum.AUTHOR_PAGE]) {
    if (LOCALE_ROUTES_CONFIG?.[RoutesEnum.AUTHOR_PAGE]?.hasOwnProperty(localePrefix)) {
      // add route with locale. example: 'en/home'
      if (localePrefix !== AllowedLangsEnum.EN) {
        routes.push({
          // @ts-ignore
          path: `${LOCALE_ROUTES_CONFIG[RoutesEnum.AUTHOR_PAGE][localePrefix]}/:authorName`,
          ...commonParams
        });
      }
    }
  }

  return routes;
}

// SECONDARY PAGES
export function getCookiePolicyRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.COOKIES_POLICY_TITLE',
      metaDesc: 'META_TAGS.COOKIES_POLICY_DESCR',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.COOKIE_POLICY, commonParams);
}

export function getLegalRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.LEGAL_NOTICE_TITLE',
      metaDesc: 'META_TAGS.LEGAL_NOTICE_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.LEGAL, commonParams);
}

export function getPrivacyPolicyRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.PRIVACY_POLICY_TITLE',
      metaDesc: 'META_TAGS.PRIVACY_POLICY_DESCR',
      allowPromoLine: true,
      appBackgroundLight: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.PRIVACY_POLICY, commonParams);
}

export function getTermsOfServicesRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.TERMS_OF_SERVICE_TITLE',
      metaDesc: 'META_TAGS.TERMS_OF_SERVICE_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.TERMS_OF_SERVICE, commonParams);
}

export function getSaasRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    redirectTo: '',
    data: {
      metaTitle: 'META_TAGS.SYSTEM',
      metaDesc: 'META_TAGS.SYSTEM',
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SAAS, commonParams);
}

export function getReleaseNotesRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.RELEASE_NOTE_TITLE',
      metaDesc: 'META_TAGS.RELEASE_NOTE_DESCR',
      appBackgroundLight: true,
      allowPromoLine: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.RELEASE_NOTES, commonParams);
}

// PRICE PAGE
export function getPricingRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    runGuardsAndResolvers: 'always',
    data: {
      metaTitle: 'META_TAGS.PRICING_TITLE',
      metaDesc: 'META_TAGS.PRICING_DESCR',
      hideRequestDemo: true,
      appBackgroundLight: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.PRICING, commonParams);
}

// SIGN UP PAGES
export function getSignUpRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    canActivate: [() => {
      const localeService = inject(LocaleService);
      return localeService.userLocale$.pipe(filter(v => !!v), take(1), map((r) => {
        return localeService.isAllowedByUserLocale() ? true : createUrlTree(localeService.getLink(RoutesEnum.NOT_AVAILABLE));
      }));
    }],
    data: {
      metaTitle: 'META_TAGS.SIGN_UP_TITLE',
      metaDesc: 'META_TAGS.SIGN_UP_DESCR',
      hideRequestDemo: true,
      hideToTopButton: true,
      hasCta: true,
      hideStartToFreeAction: true,
      headerTransparent: true,
      headerLoginOnly: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.START, commonParams);
}

export function getCreateUserRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.WELCOME',
      metaDesc: 'META_TAGS.WELCOME',
      hideRequestDemo: true,
      hideToTopButton: true,
      noindex: true,
      headerTransparent: true,
      hideFooter: true,
      headerSupportOnly: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.CREATE_USER, commonParams);
}

export function getWorkspaceCreatedRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.WELCOME',
      metaDesc: 'META_TAGS.WELCOME',
      hideRequestDemo: true,
      hideToTopButton: true,
      headerTransparent: true,
      hideFooter: true,
      headerSupportOnly: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.WORKSPACE_CREATED, commonParams);
}

export function getSignInRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'META_TAGS.SIGN_IN',
      metaDesc: 'META_TAGS.SIGN_IN',
      hideRequestDemo: true,
      hideToTopButton: true,
      headerTransparent: true,
      headerRegisterOnly: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SIGN_IN, commonParams);
}

export function getNotAvailableRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    canActivate: [() => {
      const localeService = inject(LocaleService);
      return !localeService.isAllowedByUserLocale() ? true : createUrlTree(localeService.getLink(RoutesEnum.START));
    }],
    data: {
      metaTitle: 'META_TAGS.NOT_AVAILABLE',
      metaDesc: 'META_TAGS.NOT_AVAILABLE',
      headerDark: true,
      noindex: true,
      headerLoginOnly: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.NOT_AVAILABLE, commonParams);
}

export function getWelcomePage(component: Type<any>): Routes {
  const defaultRoute: Route = { path: RoutesEnum.WELCOME, component };
  const routes: Routes = [defaultRoute];
  Object.values(AllowedLangsEnum).forEach((locale: AllowedLangsEnum) => {
    if (locale !== AllowedLangsEnum.EN) {
      routes.push({
        ...defaultRoute,
        path: `${LOCALE_ROUTES_CONFIG?.[RoutesEnum.WELCOME]?.[locale]}`,
        data: {
          metaTitle: '',
          metaDesc: '',
        }
      });
    }
  });
  return routes;
}

// EXTERNAL PAGES

export function getTradeOfferRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple offer',
      metaDesc: 'Runple offer',
      isExternalPage: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.OFFER, commonParams);
}

export function getMicrosoftConfirmationRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple',
      metaDesc: 'Runple',
      noindex: true,
      isExternalPage: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.MICROSOFT_CONFIRMATION, commonParams);
}

export function getFeedbackRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple feedback',
      metaDesc: 'Runple feedback',
      isExternalPage: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SURVEY, commonParams);
}

export function getShopifyConnectionRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple - connect Shopify',
      metaDesc: 'Runple - connect Shopify',
      isExternalPage: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SHOPIFY_CONNECTION, commonParams);
}

export function getShopifyInstallRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple - install Shopify',
      metaDesc: 'Runple - install Shopify',
      isExternalPage: true,
      noindex: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SHOPIFY_INSTALL, commonParams);
}

export function getShopifyOauthRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple - oauth Shopify',
      metaDesc: 'Runple - oauth Shopify',
      isExternalPage: true,
      hideRequestDemo: true,
      noindex: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.SHOPIFY_OAUTH, commonParams);
}

export function getEmailSubscriptionRoutes(loadChildren: LoadChildren): Routes {
  const commonParams: Partial<Route> = {
    loadChildren,
    data: {
      metaTitle: 'Runple email subscription',
      metaDesc: 'Runple email subscription',
      isExternalPage: true,
      hideRequestDemo: true,
      hideToTopButton: true,
    }
  };
  return getLazyModuleRoutes(RoutesEnum.EMAIL_SUBSCRIPTION, commonParams);
}

